import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Editor from "@monaco-editor/react";
import {
  PackageDetailDto,
  UpdatePackageCommand,
} from "../../api/models/PackageDto";
import { getPackageById, updatePackage } from "../../api/adminPackageClient";
import styles from "./PackageEditForm.module.scss";

interface PackageEditFormProps {
  packageId: string;
  onSave?: () => void;
}

const PackageEditForm = ({
  packageId,
  onSave,
}: PackageEditFormProps): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packageData, setPackageData] = useState<PackageDetailDto | null>(null);
  const [jsonErrorConfig, setJsonErrorConfig] = useState<string | null>(null);
  const [jsonErrorCleanup, setJsonErrorCleanup] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    published: false,
    configurationStrategy: "",
    cleanupConfigurationStrategy: "",
    moreInfo: "",
    customFields: "",
  });

  useEffect(() => {
    const fetchPackageData = async () => {
      setLoading(true);
      try {
        const data = await getPackageById(packageId);
        setPackageData(data);
        setFormData({
          name: data.name || "",
          description: data.description || "",
          published: data.published,
          configurationStrategy: data.configurationStrategy ?? "",
          cleanupConfigurationStrategy: data.cleanupConfigurationStrategy ?? "",
          moreInfo: data.moreInfo || "",
          customFields: data.customFields ?? "",
        });
      } catch (error) {
        console.error("Failed to fetch package data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (packageId) {
      fetchPackageData();
    }
  }, [packageId]);

  // Configure Monaco JSON schema validation
  useEffect(() => {
    // Import monaco dynamically to configure JSON schemas
    import("monaco-editor")
      .then((monaco) => {
        // Define the JSON schema for Configuration Strategy
        const configurationStrategySchema = {
          type: "object",
          properties: {
            Steps: {
              type: "array",
              description: "Array of configuration steps",
              items: {
                type: "object",
                properties: {
                  Service: {
                    type: "string",
                    description: "The service name for this step",
                  },
                  Operation: {
                    type: "string",
                    description: "The operation to perform",
                  },
                  Args: {
                    type: "object",
                    description: "Arguments for the operation",
                    additionalProperties: true,
                  },
                },
                required: ["Service", "Operation"],
                additionalProperties: true,
              },
            },
          },
          additionalProperties: true,
        };

        // Configure Monaco's JSON language service
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: true,
          schemas: [
            {
              uri: "http://myserver/configuration-strategy-schema.json",
              fileMatch: ["*"], // Associate with all JSON files in this context
              schema: configurationStrategySchema,
            },
          ],
        });
      })
      .catch((error) => {
        console.warn("Failed to configure Monaco JSON schema:", error);
      });
  }, []);

  const validateConfigurationStrategy = (jsonString: string): string | null => {
    const trimmed = jsonString.trim();

    // Allow empty JSON or just braces
    if (!trimmed || trimmed === "{}") {
      return null; // Empty is valid
    }

    try {
      const parsed = JSON.parse(jsonString);

      // Check if it's an object
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        return "Configuration Strategy must be a JSON object";
      }

      // If it's just an empty object, it's valid
      if (Object.keys(parsed).length === 0) {
        return null;
      }

      // If it has properties, validate the structure
      if (Object.prototype.hasOwnProperty.call(parsed, "Steps")) {
        // Check if Steps is an array
        if (!Array.isArray(parsed.Steps)) {
          return "'Steps' property must be an array";
        }

        // Validate each step in the Steps array
        for (let i = 0; i < parsed.Steps.length; i++) {
          const step = parsed.Steps[i];

          if (
            typeof step !== "object" ||
            step === null ||
            Array.isArray(step)
          ) {
            return `Step ${i + 1} must be an object`;
          }

          if (!Object.prototype.hasOwnProperty.call(step, "Service")) {
            return `Step ${i + 1} must have a 'Service' property`;
          }

          if (!Object.prototype.hasOwnProperty.call(step, "Operation")) {
            return `Step ${i + 1} must have an 'Operation' property`;
          }

          if (typeof step.Service !== "string") {
            return `Step ${i + 1}: 'Service' must be a string`;
          }

          if (typeof step.Operation !== "string") {
            return `Step ${i + 1}: 'Operation' must be a string`;
          }
        }
      }

      return null; // Valid JSON with correct structure
    } catch (error) {
      return "Invalid JSON format";
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(formData.configurationStrategy);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData((prev) => ({
        ...prev,
        configurationStrategy: formatted,
      }));
      setJsonErrorConfig(null);
    } catch (error) {
      setJsonErrorConfig("Cannot format invalid JSON");
    }
  };

  const formatCleanupJson = () => {
    try {
      const parsed = JSON.parse(formData.cleanupConfigurationStrategy);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData((prev) => ({
        ...prev,
        cleanupConfigurationStrategy: formatted,
      }));
    } catch (error) {
      console.error("Cannot format invalid JSON for Cleanup Configuration");
    }
  };

  const formatCustomFieldsJson = () => {
    try {
      const parsed = JSON.parse(formData.customFields);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormData((prev) => ({
        ...prev,
        customFields: formatted,
      }));
    } catch (error) {
      console.error("Cannot format invalid JSON for Custom Fields");
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear JSON errors when user types in other fields
    if (field !== "configurationStrategy" && jsonErrorConfig) {
      setJsonErrorConfig(null);
    }
    if (field !== "cleanupConfigurationStrategy" && jsonErrorCleanup) {
      setJsonErrorCleanup(null);
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    const newValue = value || "";
    setFormData((prev) => ({
      ...prev,
      configurationStrategy: newValue,
    }));

    // Validate JSON as user types (with debounce effect)
    const error = validateConfigurationStrategy(newValue);
    setJsonErrorConfig(error);
  };

  const handleCleanupEditorChange = (value: string | undefined) => {
    const newValue = value || "";
    setFormData((prev) => ({
      ...prev,
      cleanupConfigurationStrategy: newValue,
    }));

    // Reuse validation for cleanup JSON as well
    const error = validateConfigurationStrategy(newValue);
    setJsonErrorCleanup(error);
  };

  const handleSave = async () => {
    // Validate before saving
    const validationError = validateConfigurationStrategy(
      formData.configurationStrategy,
    );
    if (validationError) {
      setJsonErrorConfig(validationError);
      return;
    }

    setSaving(true);
    try {
      const normalizeJsonField = (value: string | undefined | null) => {
        if (value === null || value === undefined) return null;
        const trimmed = value.trim();
        if (!trimmed) return null;
        if (trimmed === "{}" || trimmed === "null") return null;
        return value;
      };

      const command: UpdatePackageCommand = {
        id: packageId,
        name: formData.name,
        description: formData.description,
        published: formData.published,
        configurationStrategy: normalizeJsonField(
          formData.configurationStrategy,
        ),
        cleanupConfigurationStrategy: normalizeJsonField(
          formData.cleanupConfigurationStrategy,
        ),
        moreInfo: formData.moreInfo,
        customFields: normalizeJsonField(formData.customFields),
      };

      await updatePackage(command);
      onSave?.();
      navigate("/admin/packages");
    } catch (error) {
      console.error("Failed to save package:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/packages");
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!packageData) {
    return <div>Package not found</div>;
  }

  return (
    <div className={styles.container}>
      <Paper sx={{ p: 3, maxWidth: "100%", margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Edit Package: {packageData.name}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Link
            href="https://toolchain.heineken.com/faq/acda31eb-378c-4d5c-643f-08de63d4bf78"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: "primary.main",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            View Documentation
            <OpenInNew sx={{ fontSize: 16 }} />
          </Link>
        </Box>

        <Box component="form" sx={{ mt: 2, width: "100%" }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              multiline
              rows={3}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <TextField
              label="More Info (HTML)"
              value={formData.moreInfo}
              onChange={(e) => handleInputChange("moreInfo", e.target.value)}
              multiline
              rows={4}
              helperText="HTML content that will be displayed when users click 'More Info' on the package. Use HTML tags for formatting."
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.published}
                onChange={(e) =>
                  handleInputChange("published", e.target.checked)
                }
              />
            }
            label="Published"
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6">
                Configuration Strategy (JSON)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Link
                  href="https://toolchain.heineken.com/faq/92621b35-1497-4985-6810-08de5a6897e6"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "primary.main",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View Documentation
                  <OpenInNew sx={{ fontSize: 16 }} />
                </Link>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={formatJson}
                  disabled={!!jsonErrorConfig}
                >
                  Format JSON
                </Button>
              </Box>
            </Box>
            {jsonErrorConfig && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {jsonErrorConfig}
              </Alert>
            )}
          </Box>
          <Box
            sx={{
              border: 1,
              borderColor: jsonErrorConfig ? "error.main" : "grey.300",
              borderRadius: 1,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Editor
              height="400px"
              defaultLanguage="json"
              value={formData.configurationStrategy}
              onChange={handleEditorChange}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                formatOnPaste: true,
                formatOnType: true,
                automaticLayout: true,
                scrollbar: {
                  vertical: "auto",
                  horizontal: "auto",
                  verticalScrollbarSize: 10,
                  horizontalScrollbarSize: 10,
                },
                overviewRulerLanes: 0,
                suggest: {
                  showKeywords: true,
                  showSnippets: true,
                  showProperties: true,
                },
                quickSuggestions: {
                  other: true,
                  comments: false,
                  strings: true,
                },
                acceptSuggestionOnEnter: "on",
                tabCompletion: "on",
                wordBasedSuggestions: "matchingDocuments",
              }}
            />
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6">
                Cleanup Configuration Strategy (JSON)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Link
                  href="https://toolchain.heineken.com/faq/92621b35-1497-4985-6810-08de5a6897e6"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "primary.main",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View Documentation
                  <OpenInNew sx={{ fontSize: 16 }} />
                </Link>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={formatCleanupJson}
                  disabled={!!jsonErrorCleanup}
                >
                  Format JSON
                </Button>
              </Box>
            </Box>
            {jsonErrorCleanup && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {jsonErrorCleanup}
              </Alert>
            )}
          </Box>
          <Box
            sx={{
              border: 1,
              borderColor: jsonErrorCleanup ? "error.main" : "grey.300",
              borderRadius: 1,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Editor
              height="300px"
              defaultLanguage="json"
              value={formData.cleanupConfigurationStrategy}
              onChange={handleCleanupEditorChange}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                formatOnPaste: true,
                formatOnType: true,
                automaticLayout: true,
                scrollbar: {
                  vertical: "auto",
                  horizontal: "auto",
                  verticalScrollbarSize: 10,
                  horizontalScrollbarSize: 10,
                },
                overviewRulerLanes: 0,
                suggest: {
                  showKeywords: true,
                  showSnippets: true,
                  showProperties: true,
                },
                quickSuggestions: {
                  other: true,
                  comments: false,
                  strings: true,
                },
                acceptSuggestionOnEnter: "on",
                tabCompletion: "on",
                wordBasedSuggestions: "matchingDocuments",
              }}
            />
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6">Custom Fields (JSON)</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={formatCustomFieldsJson}
              >
                Format JSON
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              border: 1,
              borderColor: "grey.300",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Editor
              height="300px"
              defaultLanguage="json"
              value={formData.customFields}
              onChange={(value: string | undefined) => {
                const newValue = value || "";
                setFormData((prev) => ({
                  ...prev,
                  customFields: newValue,
                }));
              }}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                formatOnPaste: true,
                formatOnType: true,
                automaticLayout: true,
                scrollbar: {
                  vertical: "auto",
                  horizontal: "auto",
                  verticalScrollbarSize: 10,
                  horizontalScrollbarSize: 10,
                },
                overviewRulerLanes: 0,
                suggest: {
                  showKeywords: true,
                  showSnippets: true,
                  showProperties: true,
                },
                quickSuggestions: {
                  other: true,
                  comments: false,
                  strings: true,
                },
                acceptSuggestionOnEnter: "on",
                tabCompletion: "on",
                wordBasedSuggestions: "matchingDocuments",
              }}
            />
          </Box>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !!jsonErrorConfig || !!jsonErrorCleanup}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default PackageEditForm;
